/**
 * Unified Anstehend fetch + chat reply — saved schedule items + Kalender.
 */

import { fetchSchoolEmails } from "../api/schoolEmails";
import { fetchScheduleItems } from "../api/scheduleItems";
import { fetchRegionalCalendarContext } from "../api/regionalCalendar";
import { addDaysYmd, formatHeuteScheduleMeta } from "./heuteScheduleBuckets";
import { buildCalendarRecallLines, formatCalendarTodayLine } from "./calendarRecallLines";
import { mergeHeuteScheduleItems } from "./heuteScheduleMerge";
import {
  ITEM_TYPE_EVENT,
  ITEM_TYPE_TASK,
  SCHEDULE_EMPTY_HINT,
} from "./scheduleLabels";
import { todayYmdZurich } from "./schoolAttention";

/** Home card: today and the next 6 days, plus overdue. */
export const CHAT_HOME_AGENDA_DAYS = 6;

/** «Was steht an?» calendar slice and older chat windows. */
export const CHAT_SCHEDULE_RECALL_DAYS = 14;

/** «Was steht an?» before the parent asks for the rest of the list. */
export const CHAT_SCHEDULE_RECALL_EXTEND_DAYS = 60;

/**
 * @param {object[]} items
 * @param {number|null} days
 * @param {string} [todayYmd]
 */
export function scheduleItemsWithinDays(items, days, todayYmd = todayYmdZurich()) {
  const list = Array.isArray(items) ? items : [];
  if (days == null) return list;
  const maxDate = addDaysYmd(todayYmd, days);
  return list.filter((item) => {
    if (item.overdue) return true;
    const date = String(item.date || "");
    return date >= todayYmd && date <= maxDate;
  });
}

/**
 * @param {object[]} savedItems
 * @param {{ days?: number|null, todayYmd?: string }} [opts]
 *        Pass `days: null` for all upcoming (no horizon cap).
 */
export function mergeScheduleItemsForChat(
  savedItems,
  { days = CHAT_SCHEDULE_RECALL_DAYS, todayYmd = todayYmdZurich() } = {},
) {
  const all = mergeHeuteScheduleItems(savedItems);
  const maxDate = days == null ? null : addDaysYmd(todayYmd, days);
  return all.filter((item) => {
    const date = String(item.date || "");
    if (!date) return false;
    if (item.overdue) return true;
    if (date < todayYmd) return false;
    if (maxDate && date > maxDate) return false;
    return true;
  });
}

async function fetchScheduleItemsForChat({ days = CHAT_SCHEDULE_RECALL_DAYS } = {}) {
  const [savedData, mailData] = await Promise.all([
    fetchScheduleItems({ days: 365 }),
    fetchSchoolEmails().catch(() => ({ items: [] })),
  ]);
  const items = mergeScheduleItemsForChat(savedData.items || [], { days });
  const hasExtractions = (mailData.items || []).length > 0;
  const hasSaved = (savedData.items || []).some((row) => !row.done_at);
  return { items, hasExtractions, hasSaved };
}

/**
 * @param {{ days?: number }} [params]
 */
export async function fetchUnifiedScheduleForChat({ days = CHAT_SCHEDULE_RECALL_DAYS } = {}) {
  const { items } = await fetchScheduleItemsForChat({ days });
  return items;
}

/**
 * Schedule rows + regional calendar lines for chat recall.
 * @param {{ days?: number }} [params]
 */
/**
 * Ferien and Feiertage lines only. Schedule rows come from the agenda already on the page.
 * @param {{ days?: number }} [params]
 */
export async function fetchCalendarRecallLines({ days = CHAT_SCHEDULE_RECALL_DAYS } = {}) {
  const calendar = await fetchRegionalCalendarContext().catch(() => null);
  const todayYmd = todayYmdZurich();
  const calendarRecall = buildCalendarRecallLines(calendar, { days, todayYmd });
  return {
    calendarTodayLine: calendarRecall.todayLine,
    calendarLines: calendarRecall.lines,
  };
}

export async function fetchScheduleRecallBundle({ days = CHAT_SCHEDULE_RECALL_DAYS } = {}) {
  const [schedule, calendar] = await Promise.all([
    fetchScheduleItemsForChat({ days }),
    fetchRegionalCalendarContext()
      .catch(() => null),
  ]);
  const todayYmd = todayYmdZurich();
  const calendarRecall = buildCalendarRecallLines(calendar, { days, todayYmd });
  return {
    items: schedule.items,
    calendarTodayLine: calendarRecall.todayLine,
    calendarLines: calendarRecall.lines,
    hasExtractions: schedule.hasExtractions,
    hasSaved: schedule.hasSaved,
    hasScheduleActivity:
      schedule.hasExtractions ||
      schedule.hasSaved ||
      schedule.items.length > 0 ||
      calendarRecall.lines.length > 0,
  };
}

/**
 * @param {object} item
 * @param {string} [todayYmd]
 */
export function formatScheduleRecallLine(item, todayYmd = todayYmdZurich()) {
  const typeLabel = item.isTask ? ITEM_TYPE_TASK : ITEM_TYPE_EVENT;
  const meta = formatHeuteScheduleMeta(item, todayYmd);
  const overduePrefix = item.overdue ? "überfällig · " : "";
  return `• ${overduePrefix}${meta} · ${typeLabel}: ${item.title}`;
}

/**
 * @param {object[]} items normalized heute schedule items
 * @param {{
 *   calendarTodayLine?: string,
 *   calendarLines?: string[],
 *   hasExtractions?: boolean,
 *   hasScheduleActivity?: boolean,
 *   days?: number,
 *   todayYmd?: string,
 * }} [opts]
 */
export function buildScheduleRecallReply(items, opts = {}) {
  const {
    calendarTodayLine = null,
    calendarLines = [],
    hasExtractions = false,
    hasScheduleActivity = false,
    days = CHAT_SCHEDULE_RECALL_DAYS,
    todayYmd = todayYmdZurich(),
  } = opts;

  const hasCalendar = calendarLines.length > 0;
  const hasPersonal = (items || []).length > 0;

  if (!hasCalendar && !hasPersonal) {
    if (hasScheduleActivity || hasExtractions) {
      return {
        text:
          days == null
            ? "Nichts Anstehendes."
            : `In den nächsten ${days} Tagen steht nichts Anstehendes. Frag mich nach einem späteren Datum.`,
      };
    }
    return {
      text: SCHEDULE_EMPTY_HINT,
      needsSchoolSetup: true,
    };
  }

  const parts = ["Das steht an:"];
  if (hasCalendar) {
    parts.push("", "Kalender", calendarTodayLine || formatCalendarTodayLine(todayYmd), ...calendarLines);
  }

  return {
    text: parts.join("\n"),
    showAgenda: hasPersonal,
    extendAgenda: hasPersonal,
  };
}
